import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

interface FieldCommentDialogProps {
  open: boolean;
  fieldPath: string | null;
  fieldLabel?: string;
  busy?: boolean;
  onClose: () => void;
  onSubmit: (text: string) => Promise<void>;
}

export function FieldCommentDialog({
  open,
  fieldPath,
  fieldLabel,
  busy,
  onClose,
  onSubmit,
}: FieldCommentDialogProps) {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    setText('');
    setError(null);
    const t = setTimeout(() => textareaRef.current?.focus(), 0);
    return () => {
      clearTimeout(t);
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !fieldPath) return null;

  const submit = async () => {
    const trimmed = text.trim();
    if (trimmed.length < 2) {
      setError('Write at least a couple of words.');
      return;
    }
    try {
      await onSubmit(trimmed);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const heading = fieldLabel || fieldPath;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="field-comment-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="field-comment-title" className="text-base font-semibold text-gray-900">
              Comment on {heading}
            </h2>
            <p className="mt-1 text-xs text-gray-500 font-mono">{fieldPath}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          className="mt-3 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder="What should change about this field?"
        />
        {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-hover disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save comment'}
          </button>
        </div>
      </div>
    </div>
  );
}
